// Unit conversion. The document always stores metres; imperial is display-only.

export type Units = 'metric' | 'imperial';

export const M_PER_FT = 0.3048;

export const mToFt = (m: number) => m / M_PER_FT;
export const ftToM = (ft: number) => ft * M_PER_FT;

/** Format a length for display in the active unit. */
export function fmtLen(m: number, units: Units): string {
  return units === 'imperial' ? `${mToFt(m).toFixed(2)} ft` : `${m.toFixed(2)} m`;
}

/** The display-unit numeric value of a metre measurement. */
export function toDisplay(m: number, units: Units): number {
  return units === 'imperial' ? mToFt(m) : m;
}

/** Convert a display-unit numeric value back to metres. */
export function fromDisplay(v: number, units: Units): number {
  return units === 'imperial' ? ftToM(v) : v;
}

export const unitLabel = (units: Units) => (units === 'imperial' ? 'ft' : 'm');

// ── Panel / product sizing ────────────────────────────────────────────────
export const IN_PER_M = 39.37007874;

export const mToIn = (m: number) => m * IN_PER_M;

/** Label for panel-scale dimensions: 'in' (imperial) or 'cm' (metric). */
export const panelUnitLabel = (units: Units) => (units === 'imperial' ? 'in' : 'cm');

/** The panel-scale numeric value of a metre measurement (cm or inches). */
export function toPanelDisplay(m: number, units: Units): number {
  return units === 'imperial' ? mToIn(m) : m * 100;
}

/** Convert a panel-scale display value (cm or inches) back to metres. */
export function fromPanelDisplay(v: number, units: Units): number {
  return units === 'imperial' ? v / IN_PER_M : v / 100;
}

export function fmtPanelLen(m: number, units: Units): string {
  return `${Math.round(toPanelDisplay(m, units))} ${panelUnitLabel(units)}`;
}

export function fmtPanelDims(units: Units, ...metres: number[]): string {
  const vals = metres.map((m) => Math.round(toPanelDisplay(m, units)));
  return `${vals.join('×')} ${panelUnitLabel(units)}`;
}
